import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { conferencesApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface Conference {
  id: string
  name: string
  number: string
  pin: string
  admin_pin: string
  max_participants: number
  announce_join_leave: boolean
  music_on_hold: boolean
  wait_for_leader: boolean
  quiet_mode: boolean
  record: boolean
  pin_required: boolean
  is_active: boolean
}

const emptyForm = {
  name: '',
  number: '',
  pin: '',
  admin_pin: '',
  max_participants: 50,
  announce_join_leave: true,
  music_on_hold: true,
  wait_for_leader: false,
  quiet_mode: false,
  record: false,
  pin_required: false,
  is_active: true
}

export default function Conferences() {
  const confirm = useConfirm()
  const [conferences, setConferences] = useState<Conference[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingConf, setEditingConf] = useState<Conference | null>(null)
  const [formData, setFormData] = useState(emptyForm)

  useEffect(() => {
    loadConferences()
  }, [])

  const loadConferences = async () => {
    try {
      const response = await conferencesApi.list()
      setConferences(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingConf(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (conf: Conference) => {
    setEditingConf(conf)
    setFormData({
      name: conf.name,
      number: conf.number,
      pin: '',
      admin_pin: '',
      max_participants: conf.max_participants || 50,
      announce_join_leave: conf.announce_join_leave ?? true,
      music_on_hold: conf.music_on_hold ?? true,
      wait_for_leader: conf.wait_for_leader ?? false,
      quiet_mode: conf.quiet_mode ?? false,
      record: conf.record ?? false,
      pin_required: conf.pin_required ?? false,
      is_active: conf.is_active
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const submitData: Record<string, unknown> = { ...formData }
      if (!submitData.pin) delete submitData.pin
      if (!submitData.admin_pin) delete submitData.admin_pin

      if (editingConf) {
        await conferencesApi.update(editingConf.id, submitData)
        toast.success('Konferenzraum aktualisiert')
      } else {
        await conferencesApi.create(submitData)
        toast.success('Konferenzraum erstellt')
      }
      closeModal()
      loadConferences()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDelete = async (conf: Conference) => {
    const confirmed = await confirm({
      title: 'Konferenzraum löschen',
      message: `Konferenzraum "${conf.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await conferencesApi.delete(conf.id)
      toast.success('Konferenzraum gelöscht')
      loadConferences()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingConf(null)
    setFormData(emptyForm)
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Konferenzräume</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neuer Konferenzraum
        </Button>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Name</th>
              <th>Max. Teilnehmer</th>
              <th>PIN</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-4">Laden...</td></tr>
            ) : conferences.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-4 text-muted">Keine Konferenzräume</td></tr>
            ) : (
              conferences.map(c => (
                <tr key={c.id}>
                  <td><strong>{c.number}</strong></td>
                  <td>{c.name}</td>
                  <td>{c.max_participants}</td>
                  <td>
                    {c.pin_required ? (
                      <i className="bi bi-lock text-warning" title="PIN erforderlich"></i>
                    ) : (
                      <i className="bi bi-unlock text-muted" title="Kein PIN"></i>
                    )}
                  </td>
                  <td><Badge bg={c.is_active ? 'success' : 'secondary'}>{c.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                  <td className="text-end">
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(c)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(c)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingConf ? 'Konferenzraum bearbeiten' : 'Neuer Konferenzraum'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nummer *</Form.Label>
                  <Form.Control
                    value={formData.number}
                    onChange={(e) => setFormData({...formData, number: e.target.value})}
                    required
                    placeholder="z.B. 800"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="z.B. Meeting-Raum 1"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Max. Teilnehmer</Form.Label>
              <Form.Control
                type="number"
                value={formData.max_participants}
                onChange={(e) => setFormData({...formData, max_participants: parseInt(e.target.value) || 50})}
                min={2}
                max={500}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="pin_required"
                label="PIN erforderlich"
                checked={formData.pin_required}
                onChange={(e) => setFormData({...formData, pin_required: e.target.checked})}
              />
            </Form.Group>

            {formData.pin_required && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{editingConf ? 'Neuer Benutzer-PIN' : 'Benutzer-PIN'}</Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.pin}
                      onChange={(e) => setFormData({...formData, pin: e.target.value})}
                      placeholder="z.B. 1234"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{editingConf ? 'Neuer Admin-PIN' : 'Admin-PIN'}</Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.admin_pin}
                      onChange={(e) => setFormData({...formData, admin_pin: e.target.value})}
                      placeholder="z.B. 9999"
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="switch"
                    id="announce_join_leave"
                    label="Beitritt/Austritt ansagen"
                    checked={formData.announce_join_leave}
                    onChange={(e) => setFormData({...formData, announce_join_leave: e.target.checked})}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="switch"
                    id="music_on_hold"
                    label="Wartemusik für ersten Teilnehmer"
                    checked={formData.music_on_hold}
                    onChange={(e) => setFormData({...formData, music_on_hold: e.target.checked})}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="switch"
                    id="wait_for_leader"
                    label="Auf Moderator warten"
                    checked={formData.wait_for_leader}
                    onChange={(e) => setFormData({...formData, wait_for_leader: e.target.checked})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="switch"
                    id="quiet_mode"
                    label="Stummschaltungsmodus"
                    checked={formData.quiet_mode}
                    onChange={(e) => setFormData({...formData, quiet_mode: e.target.checked})}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="switch"
                    id="record"
                    label="Konferenz aufzeichnen"
                    checked={formData.record}
                    onChange={(e) => setFormData({...formData, record: e.target.checked})}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="switch"
                    id="is_active"
                    label="Aktiv"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingConf ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
